import { Checkbox } from '@melements/front-components';
import { AccountsContext } from 'context/accounts/AccountsContext';
import { RolesContext } from 'context/roles/RolesContext';
import { FormApi } from 'final-form';
import React, { Fragment, useContext, useState } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Redirect } from 'react-router';
import { Loader, LocaleContext, ModalForm, NotificationsContext, NotificationTypes } from 'shared';
import { useQuery } from 'shared/hooks';
import { Account, OWNER_ROLE, UpdateApplicationPayload } from 'store/api/types';

import { AddTppLogo } from '../../../shared/logo/AddTppLogo';
import { getEnvironmentVar } from '../../../utils';
import { getErrorMessage } from '../../../utils/getErrorMessage/getErrorMessage';
import { ApplicationsWrapper } from '../applications/Applications.styles';

import { ApplicationWrapper, DeleteModalWrapper } from './Application.styles';
import { ApplicationProps } from './Application.types';
import { Description, Environment, Name, RedirectUrls } from './applicationForm/fields';
import { ApplicationHeader } from './applicationHeader/ApplicationHeader';
import { General } from './general/General';
import { HowToTest } from './howToTest/HowToTest';
import { IntegrationData } from './integrationData/IntegrationData';

export const Application = ({
  onFetchApplication,
  onUpdateApplication,
  onRemoveApplication,
  match,
}: ApplicationProps) => {
  const accountsContext = useContext(AccountsContext);
  const localeContext = useContext(LocaleContext);
  const notificationsContext = useContext(NotificationsContext);
  const rolesContext = useContext(RolesContext);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const { response, error, loading, handleFetch } = useQuery(onFetchApplication);
  const handleRefetch = () => handleFetch(true);
  const activeAccount = accountsContext.activeAccount as Account;
  const hasOwnership = activeAccount && rolesContext.getRoleInAccount(activeAccount.uuid) === OWNER_ROLE;
  const [isLogoDeletedChecked, setIsLogoDeleted] = useState(false);
  const statusesForHidingDeleteLogo = ['REJECTED', 'NOT_PROVIDED', undefined, null];
  const isLogoEnabled = getEnvironmentVar('REACT_APP_LOGO_ENABLED') === 'true';

  const handleEdit = async (onSuccess: () => void, values: UpdateApplicationPayload, form: FormApi) => {
    try {
      const payload = values;
      payload.redirectUris = payload.redirectUris ? payload.redirectUris.map((entry: any) => entry.label) : [];
      payload.logoEncoded = isLogoDeletedChecked ? undefined : payload.logoEncoded;
      payload.isLogoDeleted = isLogoDeletedChecked;

      return await onUpdateApplication(payload, form, () => {
        onSuccessCallback('APPLICATION.FORM.EDIT-SUCCESS', onSuccess, form, () => setEditModalVisible(false));
      });
    } catch (error) {
      showNotificationError(error);
    }
  };

  const handleDelete = async (onSuccess: () => void, values: object, form: FormApi) => {
    try {
      return await onRemoveApplication({}, form, () => {
        onSuccessCallback('APPLICATION.FORM.REMOVE-SUCCESS', onSuccess, form, () => setDeleteModalVisible(false));
      });
    } catch (error) {
      showNotificationError(error);
    }
  };

  const onSuccessCallback = (message: string, onSuccess: () => void, form: FormApi, setModalVisible: () => void) => {
    setIsLogoDeleted(false);
    form.reset();
    setModalVisible();
    onSuccess();

    showNotificationSuccess(message);
  };

  const showNotificationSuccess = (message: string) => {
    notificationsContext.showNotification({
      message: localeContext.formatMessage({ id: message }),
      time: 1000,
      type: NotificationTypes.SUCCESS,
    });
  };

  const showNotificationError = (errorResponse: any) => {
    notificationsContext.showNotification({
      message: getErrorMessage(errorResponse, localeContext),
      time: 1000,
      type: NotificationTypes.ERROR,
    });
  };

  const handleRedirect = () => {
    setRedirect(true);
  };

  const handleEditClosing = () => {
    setEditModalVisible(false);
    setIsLogoDeleted(false);
  };

  const onChange = () => {
    setIsLogoDeleted(!isLogoDeletedChecked);
  };

  const DeleteLogoCheckbox = () => {
    return response && response.payload && statusesForHidingDeleteLogo.includes(response.payload.logoStatus) ? (
      <div />
    ) : (
      <Field name="isLogoDeleted" type="checkbox">
        {({ input, meta }: { meta: FieldRenderProps<HTMLInputElement>['meta']; input: any }) => (
          <Checkbox
            label={localeContext.formatMessage({ id: 'FORM.DELETE_LOGO' })}
            input={{
              ...input,
              checked: isLogoDeletedChecked,
              onChange,
            }}
            meta={meta}
            className="application-form__isLogoDeleted"
          />
        )}
      </Field>
    );
  };

  const LogoEditingWrapper = () => {
    return isLogoEnabled ? (
      <div>
        {isLogoDeletedChecked ? <div /> : <AddTppLogo labelValue="FORM.NEW-TPP-LOGO-UPLOAD" />}
        <DeleteLogoCheckbox />
      </div>
    ) : (
      <div />
    );
  };

  return (
    <ApplicationWrapper>
      {loading && <Loader />}

      {error && <Redirect to="/" />}
      {redirect && <Redirect to={`/${match.params.accountId}/dashboard`} />}
      {!loading && !error && response && (
        <ApplicationsWrapper className="application">
          <ApplicationHeader
            name={response.payload.name}
            env={response.payload.environment}
            backUrl={`/${match.params.accountId}/dashboard`}
            createdAt={response.payload.createdAt}
            onDelete={hasOwnership ? setDeleteModalVisible.bind(undefined, true) : undefined}
          />
          <General
            {...response.payload}
            onEdit={hasOwnership ? setEditModalVisible.bind(undefined, true) : undefined}
          />
          <IntegrationData
            clientId={response.payload.clientId}
            accountId={match.params.accountId}
            applicationId={match.params.applicationId}
          />
          <HowToTest {...response.payload} />
        </ApplicationsWrapper>
      )}

      {response && (
        <Fragment>
          <ModalForm
            className="application-form"
            initialValues={{
              ...response.payload,
              redirectUris: response.payload.redirectUris
                ? response.payload.redirectUris.map((entry: any, index: number) => ({
                    label: entry,
                    value: index.toString(),
                  }))
                : [],
            }}
            name={localeContext.formatMessage({ id: 'APPLICATION.FORM.EDIT-APP' })}
            submitName={localeContext.formatMessage({ id: 'GENERAL.SAVE' })}
            isOpen={editModalVisible}
            onClose={handleEditClosing.bind(undefined, null)}
            onSubmit={handleEdit.bind(undefined, handleRefetch)}
          >
            <Environment options={[response.payload.environment]} />
            <Name />
            <Description />
            <RedirectUrls />
            <LogoEditingWrapper />
          </ModalForm>
        </Fragment>
      )}
      {response && (
        <Fragment>
          <DeleteModalWrapper>
            <ModalForm
              className="application-remove-form"
              initialValues={{
                ...response.payload,
                redirectUris: response.payload.redirectUris
                  ? response.payload.redirectUris.map((entry: any, index: number) => ({
                      label: entry,
                      value: index.toString(),
                    }))
                  : [],
              }}
              name={localeContext.formatMessage({ id: 'APPLICATION.FORM.DELETE-APP' })}
              submitName={localeContext.formatMessage({ id: 'GENERAL.REMOVE' })}
              isOpen={deleteModalVisible}
              onClose={setDeleteModalVisible.bind(undefined, false)}
              onSubmit={handleDelete.bind(undefined, handleRedirect)}
            >
              {response.payload.name} {localeContext.formatMessage({ id: 'GENERAL.REMOVE-MODAL-TEXT' })}
            </ModalForm>
          </DeleteModalWrapper>
        </Fragment>
      )}
    </ApplicationWrapper>
  );
};
